import {
	buildModel,
	comparison,
	currentStreak,
	type DateKey,
	isComplete,
	levelName,
	solveOutcomes,
} from '@arrow/game-engine';
import { LEVELS, type Puzzle } from '@arrow/puzzle-schema';
import { type CSSProperties, lazy, type ReactNode, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { DailyLevelSummary } from '../api/client.ts';
import type { Handoff } from '../app/handoff.ts';
import { navigate } from '../app/router.tsx';
import { useGameLayout } from '../app/useMedia.ts';

import { prefersReducedMotion } from '../game/haptics.ts';
import { type GameAction, mapKey } from '../game/keyboard.ts';
import { type GameSession, type Phase, useGame } from '../game/useGame.ts';
import { type Persistence, slotFor } from '../storage/persistence.ts';
import { weekdayKey } from '../game/view.ts';
import { useDevMode } from '../dev/mode.ts';
import { notePlayed } from '../push/push.ts';
import type { DevControls } from '../dev/types.ts';
import { AppMenu, type MenuItem } from './AppMenu.tsx';
import type { BackTo } from './BackPill.tsx';
import { Board } from './Board.tsx';
import { useCamera, useWholeBoard } from './camera/CameraStage.tsx';
import { isBigBoard } from './camera/geometry.ts';
import { ClueBar, FinishedFoot } from './ClueBar.tsx';
import { ClueList, KeysIcon } from './ClueList.tsx';
import { Header } from './Header.tsx';
import { Icon } from './icons.tsx';
import { Keyboard } from './Keyboard.tsx';
import { PaceTrack } from './PaceTrack.tsx';
import { primaryVariant, type ResultData, ResultSheet, shareFromResultDetail, type WayOn, waiting } from './ResultSheet.tsx';
import '../styles/game-layout.css';

/** The poster's fade-in (result.css); the board's finished layout lands under it after. */
const POSTER_COVER_MS = 450;

/** The dev tools: a lazily loaded chunk, fetched only once dev mode is on (dev/mode.ts). */
const DevPanelChunk = lazy(() => import('../dev/DevPanel.tsx'));

interface GameProps {
	puzzle: Puzzle;
	session: GameSession;
	todayKey: DateKey;
	persistence: Persistence;
	dev?: DevControls | undefined;
	/** "‹ Today" (`/`) or, for a practice puzzle, "‹ Play" (`/play`). */
	back?: BackTo | undefined;
	/** Today's daily: the day's levels, so the poster can say what's left today. */
	dayLevels?: readonly DailyLevelSummary[] | null | undefined;
	/** Arriving from Today: the hero's box and the ghost's clue (the hand-over). */
	intro?: Handoff | null | undefined;
	/** An older daily: labelled "From the archive", never for the streak. */
	archive?: boolean | undefined;
	/** A first visit from a share: one line over the masthead until the first letter. */
	welcome?: ReactNode;
	/** Opened from Play: the next in its stack or search, asked as the poster opens. */
	playNext?: (() => WayOn['next']) | undefined;
	/** The puzzle has just been solved here (not one opened already solved). */
	onSolved?: (() => void) | undefined;
	/** A one-off line on the clue band as the game opens ("You’re in Family"). */
	notice?: string | null | undefined;
}

/** The hand-over's flight (ms): the hero board becomes the game board. */
export const HANDOVER_MS = 520;

export function Game({ puzzle, session, todayKey, persistence, dev, back = '/', dayLevels, intro, archive, welcome, playNext, onSolved, notice }: GameProps) {
	const model = useMemo(() => buildModel(puzzle), [puzzle]);
	// The finished board: no keyboard and no clue band (nothing is left to type), the
	// board takes the room, and a quiet foot reads clues back. A solved puzzle opens like
	// this; after a solve it's laid out under the poster, so the poster lifts off a board
	// already in place.
	const appRef = useRef<HTMLDivElement>(null);
	const stageRef = useRef<HTMLElement>(null);
	const [phaseSeen, setPhaseSeen] = useState<Phase | null>(null);
	const [covered, setCovered] = useState(false);
	const [reviewing, setReviewing] = useState(false);
	// 10×10 and up: a one-line masthead, and the board at reading size. On a phone it's
	// read through a camera (Flow navigation then prefers an answer already in frame);
	// where the whole board fits at that size, the column widens and there's no camera.
	const big = isBigBoard(model);
	// Past the phone (J3): a tablet's wider column, or, wide with a mouse, two columns (the
	// board, then the clue band and the clue list, the drawn keys behind "Show keys").
	const layout = useGameLayout();
	const wide = layout === 'wide';
	const [keysShown, setKeysShown] = useState(false);
	// (The phase as of this render: `useGame` comes after, and this settles in the same pass.)
	const laidOutFinished = phaseSeen === 'done' || (phaseSeen === 'card' && covered);
	const whole = useWholeBoard(big, model, appRef, stageRef, laidOutFinished, wide);
	const cameraOn = big && whole === null;
	const camera = useCamera();
	const { snap, actions } = useGame({
		model,
		session,
		todayKey,
		persistence,
		visibleCells: cameraOn ? camera.ctl.visibleCells : undefined,
		initialAnswer: intro?.ghost ?? null,
	});
	const { game, flow, stats, phase } = snap;
	// Phase changes, folded in during render so the layout never lags a frame: a reset
	// brings the keyboard back; leaving the finished board forgets the clue being read.
	if (phase !== phaseSeen) {
		setPhaseSeen(phase);
		if (phase === 'playing' || phase === 'finishing') setCovered(false);
		// A finished board is laid out finished: opening the poster over it (See result) keeps
		// the foot, and so its See result, where it is, so Escape can hand focus back to it
		// as back does (QA2-10). It used to unmount for the poster's first 450ms.
		if (phase === 'done') setCovered(true);
		if (phase !== 'done') setReviewing(false);
	}
	// The poster is up and opaque: lay the board out finished behind it.
	useEffect(() => {
		if (phase !== 'card' || covered) return;
		const id = window.setTimeout(() => setCovered(true), prefersReducedMotion() ? 0 : POSTER_COVER_MS);
		return () => window.clearTimeout(id);
	}, [phase, covered]);
	const finished = phase === 'done' || (phase === 'card' && covered);
	// The daily's day sets the art direction (never the rules); practice puzzles use today.
	const dayKey = session.dateKey ?? todayKey;
	const menuButton = useRef<HTMLButtonElement | null>(null);
	const [devOpen, setDevOpen] = useState(false);
	const DevPanel = useDevMode() ? DevPanelChunk : null;

	const streak = currentStreak(stats, todayKey);

	// Physical keyboard. Letters, Backspace and arrows reach the game from anywhere that
	// isn't a text field, menu or dialog; Tab / Space / Enter only from the grid itself
	// (elsewhere they keep their native meaning). Escape leaves the grid.
	// On a finished board, moving the selection (arrows, Enter, a tap) reads that clue back,
	// and Tab is the browser's again: nothing is left to type, and it reaches the foot (UX-032).
	const onAction = useCallback(
		(a: GameAction) => {
			if (phase === 'done' && a.type !== 'letter' && a.type !== 'backspace') setReviewing(true);
			actions.dispatch(a);
		},
		[actions, phase],
	);
	const onTapCell = useCallback(
		(cell: number) => {
			if (phase === 'done') setReviewing(true);
			actions.tapCell(cell);
		},
		[actions, phase],
	);
	const onTapClue = useCallback(
		(clueId: string) => {
			if (phase === 'done') setReviewing(true);
			actions.tapClue(clueId);
		},
		[actions, phase],
	);
	const reviewId = phase === 'done' && reviewing ? (game.cursor?.answerId ?? null) : null;
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.defaultPrevented) return;
			if (DevPanel && e.key === '`') {
				e.preventDefault();
				setDevOpen((o) => !o);
				return;
			}
			const target = e.target instanceof HTMLElement ? e.target : document.body;
			if (target.closest('input, textarea, select, [contenteditable], [role="menu"], [role="dialog"], .dev-panel')) return;
			const onSurface = target.closest('[data-game-surface]') !== null;
			if (e.key === 'Escape' && onSurface) {
				menuButton.current?.focus();
				return;
			}
			if (e.key === 'Tab' && phase === 'done') return;
			const action = mapKey(e);
			if (!action) return;
			if ((action.type === 'nextAnswer' || action.type === 'toggleDirection') && !onSurface) return;
			e.preventDefault();
			onAction(action);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onAction, DevPanel, phase]);

	// With a mouse and a physical keyboard, start with the grid focused so Tab, Space and
	// Enter work at once. Touch devices keep focus where it is (it would only draw a ring).
	useEffect(() => {
		if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches && document.activeElement === document.body) {
			document.querySelector<HTMLElement>('[data-game-surface]')?.focus({ preventScroll: true });
		}
	}, []);

	// --- Arriving from Today ------------------------------------------------------------
	// `flip`: the hero board becomes this board (one transform, from where the hero was),
	// the clues print outward from the first clue, the masthead and keys arrive. `fade`
	// (reduced motion, or no hero to fly from): a cross-fade. The result opens on its poster.
	const [introMode, setIntroMode] = useState<'flip' | 'fade' | null>(() =>
		!intro ? null : intro.openResult || !intro.rect || prefersReducedMotion() ? 'fade' : 'flip',
	);
	const introOnce = useRef(intro);
	useLayoutEffect(() => {
		const h = introOnce.current;
		if (!h) return;
		introOnce.current = null;
		if (h.openResult) {
			actions.openCard();
			return;
		}
		const board = appRef.current?.querySelector<HTMLElement>('.board');
		if (!board || !h.rect || prefersReducedMotion() || typeof board.animate !== 'function') return;
		const r1 = board.getBoundingClientRect();
		if (!r1.width || !board.offsetWidth) return;
		// In the board's own units: a camera (big boards) scales everything inside it.
		const local = board.offsetWidth / r1.width;
		const dx = (h.rect.left + h.rect.width / 2 - (r1.left + r1.width / 2)) * local;
		const dy = (h.rect.top + h.rect.height / 2 - (r1.top + r1.height / 2)) * local;
		const k = h.rect.width / r1.width;
		board.animate([{ transform: `translate(${dx}px, ${dy}px) scale(${k})` }, { transform: 'none' }], {
			duration: big ? HANDOVER_MS + 120 : HANDOVER_MS,
			easing: 'cubic-bezier(.3,.8,.25,1)',
			delay: 60,
			fill: 'backwards',
		});
		// The clues print outward from your first clue, like ink spreading from where you touched.
		const first = game.cursor?.answerId;
		const clue = first ? model.clues[model.answers[first]?.clueId ?? ''] : undefined;
		const r0 = clue?.cell.row ?? 0;
		const c0 = clue?.cell.col ?? 0;
		for (const cl of board.querySelectorAll<HTMLElement>('.cl')) {
			const cell = Number(cl.parentElement?.id.replace('cell-', '') ?? 0);
			const d = Math.abs(Math.floor(cell / model.width) - r0) + Math.abs((cell % model.width) - c0);
			cl.style.setProperty('--pd', `${220 + d * 34}ms`);
		}
		board.classList.add('printing');
		window.setTimeout(() => board.classList.remove('printing'), 2600);
		// Once, on arrival.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(() => {
		if (!introMode) return;
		const id = window.setTimeout(() => setIntroMode(null), 1200);
		return () => window.clearTimeout(id);
	}, [introMode]);

	// The welcome line stays until the first letter typed here: plain words, nothing to
	// press (UX-037). A game opened part-done keeps it until its next letter.
	const filled = game.entries.reduce((n, e) => n + (e ? 1 : 0), 0);
	const [filledAtOpen] = useState(filled);
	const showWelcome = !!welcome && filled === filledAtOpen && phase === 'playing';

	const complete = isComplete(game);
	// Solved here (it wasn't when it opened): tell the screen, once.
	const [completeAtOpen] = useState(complete);
	const solvedRef = useRef(onSolved);
	solvedRef.current = onSolved;
	const todaysDaily = session.kind === 'daily' && session.dateKey === todayKey;
	useEffect(() => {
		if (!complete || completeAtOpen) return;
		solvedRef.current?.();
		// Today's daily, done: notifications (if on here) learn the day and the streak, so no
		// reminder comes after you've played (push/push.ts). After the stats are saved.
		if (todaysDaily) window.setTimeout(() => void notePlayed(), 0);
	}, [complete, completeAtOpen, todaysDaily]);

	// Check and Reveal act on the board, so focus goes back to the board when ≡ closes, where
	// typing the fix works (QA4-8). Closing ≡ without acting still returns to ≡. With a mouse
	// and keyboard only: on touch the drawn keys type wherever focus is.
	const backToGrid = useCallback((act: () => void) => () => {
		act();
		const btn = menuButton.current;
		if (!btn || !window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;
		const onFocus = () => {
			done();
			document.querySelector<HTMLElement>('[data-game-surface]')?.focus({ preventScroll: true });
		};
		const timer = window.setTimeout(() => done(), 2000);
		const done = () => {
			window.clearTimeout(timer);
			btn.removeEventListener('focus', onFocus);
		};
		btn.addEventListener('focus', onFocus);
	}, []);
	// How each cell fell, for the share's emoji board (spoiler-free: no letters).
	const outcomes = useMemo(() => (complete ? solveOutcomes(model, game, flow) : undefined), [complete, model, game, flow]);
	// This puzzle's help, first in ≡. A solved puzzle has none to give (and See result is
	// the finished board's own foot), so the section goes.
	// A perfect solve is no checks and no reveals, so while it's still perfect the section says
	// (once) that any of them ends one; after that the warning means nothing (QA3-1).
	const perfect = game.checksUsed === 0 && game.revealsUsed === 0;
	const puzzleItems: MenuItem[] = complete
		? []
		: [
				{ label: 'Check this answer', icon: <Icon.checkOne />, onSelect: backToGrid(() => actions.check('answer')), disabled: !game.cursor },
				{ label: 'Check the puzzle', icon: <Icon.checkAll />, onSelect: backToGrid(() => actions.check('puzzle')) },
				{ label: 'Reveal this answer', icon: <Icon.reveal />, onSelect: backToGrid(actions.reveal), disabled: !game.cursor },
			];

	// The poster's way on, below its crop line: home, and on today's daily what's left today.
	const wayOn: WayOn = {
		back,
		next: phase === 'card' || phase === 'done' ? (nextLevel(session, todayKey, dayLevels, persistence) ?? playNext?.() ?? null) : null,
	};

	const result: ResultData = {
		number: session.number,
		title: puzzle.title,
		type: puzzle.type,
		dateKey: session.dateKey,
		game,
		bestFlow: flow.best,
		segments: flow.segments,
		streak,
		comparison: session.kind === 'daily' && session.dateKey ? comparison(stats, session.dateKey, session.level) : null,
		level: session.level,
		kind: session.kind,
		puzzleId: session.puzzleId,
		outcomes,
		todayKey,
		challenge: session.kind === 'daily' ? (session.challenge ?? null) : null,
	};
	const primary = primaryVariant(result);

	const step = useCallback((delta: 1 | -1) => actions.dispatch({ type: 'nextAnswer', delta }), [actions]);
	const toggle = useCallback(() => actions.dispatch({ type: 'toggleDirection' }), [actions]);
	// The drawn keys are a phone's only keyboard, and a screen reader stays on the key, not
	// the grid: each press says what it did ("S, 2 of 5"). Physical keys stay quiet (UX-033).
	const keyboardAction = useCallback(
		(a: GameAction) => {
			actions.dispatch(a, { echo: true });
		},
		[actions],
	);
	const gridLabel =
		session.number !== null
			? `Sparrow number ${session.number}${session.kind !== 'puzzle' && session.level ? `, ${levelName(session.level)}` : ''}`
			: `Sparrow: ${puzzle.title}`;

	const stage = (
		<main ref={stageRef} className={cameraOn ? 'stage big' : 'stage'}>
			<p id="grid-help" className="sr-only">
				Type letters to fill the selected answer. Arrow keys move, Tab and Enter go to the next clue, Space switches direction,
				Escape leaves the grid.
			</p>
			<Board
				model={model}
				snap={snap}
				label={gridLabel}
				onTapCell={onTapCell}
				onTapClue={onTapClue}
				big={big}
				camera={cameraOn ? camera : undefined}
				reviewId={reviewId}
			/>
		</main>
	);
	const foot = (
		<FinishedFoot
			model={model}
			game={game}
			reviewId={reviewId}
			onOpenCard={actions.openCard}
			shareLabel={primary === 'rematch' ? 'Rematch' : primary === 'challenge' ? 'Challenge' : 'Share'}
			dare={primary !== 'result'}
			onShare={() => shareFromResultDetail(result, model.width, primary)}
			review={!wide}
		/>
	);
	const clueBar = (
		<ClueBar
			model={model}
			snap={snap}
			onStep={step}
			onToggle={toggle}
			onOpenCard={actions.openCard}
			onDismissHint={actions.dismissHint}
			notice={notice}
		/>
	);
	const keyboard = <Keyboard onAction={keyboardAction} disabled={complete} axis={game.cursor ? model.answers[game.cursor.answerId]?.axis : null} />;

	return (
		<div
			ref={appRef}
			className="app"
			data-phase={phase}
			data-weekday={weekdayKey(dayKey)}
			data-type={puzzle.type}
			data-board={big ? (cameraOn ? 'camera' : 'whole') : undefined}
			data-finished={finished ? '' : undefined}
			data-intro={introMode ?? undefined}
			data-layout={layout}
			style={
				whole !== null
					? ({
							[wide ? '--game-board-col' : '--column-max']: `${whole.column}px`,
							'--read-cell': `${whole.cell}px`,
							'--read-cell-narrow': `${whole.cell}px`,
						} as CSSProperties)
					: undefined
			}
		>
			{showWelcome && <p className="welcome">{welcome}</p>}
			<Header
				number={session.number}
				level={session.kind === 'puzzle' ? undefined : session.level}
				archive={archive}
				back={back}
				title={puzzle.title}
				season={puzzle.type}
				dateKey={dayKey}
				todayKey={todayKey}
				game={game}
				streak={streak}
				menu={<AppMenu screen="game" puzzle={puzzleItems} puzzleNote={perfect ? 'Any of these ends a perfect solve.' : undefined} dev={DevPanel ? () => setDevOpen(true) : undefined} buttonRef={menuButton} />}
				compact={big && !wide}
				wide={wide}
				pace={
					session.kind === 'daily' && session.challenge && (phase === 'playing' || phase === 'finishing') ? (
						<PaceTrack game={game} total={model.answerOrder.length} challenge={session.challenge} />
					) : undefined
				}
				zoom={
					cameraOn && camera.view.canZoom && (phase === 'playing' || phase === 'done')
						? { whole: camera.view.level === 'whole', onToggle: camera.ctl.toggle }
						: undefined
				}
			/>
			{wide ? (
				<div className="game-body">
					{/* The clue column comes first in the page's order (it sits right of the board):
					    from ≡, Tab reaches the clue band and Show keys before the grid, which then
					    keeps Tab for the next clue. */}
					<aside className="game-side" aria-label="Clues and keys">
						{finished ? foot : <section className="controls" aria-label="Clue">{clueBar}</section>}
						<ClueList model={model} game={game} activeId={finished ? reviewId : (game.cursor?.answerId ?? null)} onPick={onTapClue} />
						{!finished && keysShown && <div className="side-keys">{keyboard}</div>}
						{!finished && (
							<div className="side-foot">
								<button
									type="button"
									className="tlink side-keys-btn"
									// The grid keeps focus (and with it Tab and Space) when this is clicked.
									onPointerDown={(e) => e.preventDefault()}
									onClick={() => setKeysShown((v) => !v)}
								>
									<KeysIcon />
									{keysShown ? 'Hide keys' : 'Show keys'}
								</button>
							</div>
						)}
					</aside>
					{stage}
				</div>
			) : (
				<>
					{stage}
					{finished ? (
						foot
					) : (
						<section className="controls" aria-label="Clue and keyboard">
							{clueBar}
							{keyboard}
						</section>
					)}
				</>
			)}
			<ResultSheet open={phase === 'card'} data={result} onClose={actions.closeCard} model={model} wayOn={wayOn} />
			<div className="sr-only" aria-live="polite" aria-atomic="true">
				{snap.announcement}
			</div>
			{DevPanel && (
				<Suspense fallback={null}>
					<DevPanel
						open={devOpen}
						onToggle={setDevOpen}
						model={model}
						snap={snap}
						actions={actions}
						session={session}
						todayKey={todayKey}
						controls={dev}
					/>
				</Suspense>
			)}
		</div>
	);
}

/**
 * On today's daily, the levels still to play today (easy → hard), and the one to go to
 * next: the first harder one left, else the first left. Null when nothing's left, or it
 * isn't today's daily.
 */
export function nextLevel(
	session: GameSession,
	todayKey: DateKey,
	dayLevels: readonly DailyLevelSummary[] | null | undefined,
	persistence: Persistence,
): WayOn['next'] {
	if (session.kind !== 'daily' || session.dateKey !== todayKey || !dayLevels || session.number === null) return null;
	const date = session.dateKey;
	const left = dayLevels.filter(
		(l) => l.level !== session.level && persistence.resumeInfo(slotFor({ kind: 'daily', date, level: l.level })).status !== 'completed',
	);
	if (left.length === 0) return null;
	const rank = (l: DailyLevelSummary) => LEVELS.indexOf(l.level);
	const here = session.level ? LEVELS.indexOf(session.level) : -1;
	const go = left.find((l) => rank(l) > here) ?? left[0]!;
	const number = session.number;
	return {
		lead: waiting(
			left.map((l) => l.level),
			`${go.width}×${go.height}`,
		),
		label: levelName(go.level),
		ariaLabel: `Play ${levelName(go.level)}`,
		onGo: () => navigate(`/daily/${number}?l=${go.level}`, { replace: true }),
	};
}
